import { wait } from '../async-utils';

/**
 * Enum representing different backoff strategies.
 */
export enum BackoffStrategy {
  /** Constant delay between retries. */
  Constant = 'constant',

  /** Linear increase in delay between retries. */
  Linear = 'linear',

  /** Exponential increase in delay between retries. */
  Exponential = 'exponential'
}

/**
 * Configuration options for the retry mechanism.
 */
export interface IRetryWithBackoffConfig<E = Error> {
  /** Maximum number of retry attempts. Defaults to 3. */
  retries?: number;

  /** Initial delay in milliseconds before the first retry. Defaults to 100. */
  initialDelay?: number;

  /** The backoff strategy to use. Defaults to Exponential. */
  strategy?: BackoffStrategy;

  /**
   * The factor by which the delay increases.
   * - For Linear: delay increases by initialDelay * factor each time.
   * - For Exponential: delay multiplies by factor each time.
   * Defaults to 2 for Exponential, 1 for Linear/Constant.
   */
  factor?: number;

  /**
   * Maximum delay in milliseconds between retries.
   * Useful to prevent excessively long waits. Defaults to Infinity.
   */
  maxDelay?: number;

  /**
   * Apply random jitter to the delay. Reduces the chance of thundering herd.
   * Value between 0 and 1, representing the percentage of the delay.
   * e.g., 0.1 means +/- 10% jitter. Defaults to 0.
   */
  jitter?: number;

  /**
   * A function to determine if a retry should be attempted based on the error.
   * Defaults to retrying on any error. Return true to retry, false to stop.
   *
   * @param error
   * @returns A boolean value indicating if the operation should be retried or
   * not.
   */
  shouldRetry?: (error: E) => boolean | Promise<boolean>;

  /**
   * A callback function executed before each retry attempt. Receives the error,
   * attempt number, and calculated delay.
   *
   * @param error - The error that caused the retry.
   * @param attempt - The current retry attempt number.
   * @param delay - The delay in milliseconds before the next attempt.
   *
   * @returns A Promise that resolves when the callback is complete, or void if
   * synchronous.
   */
  onRetry?: (error: E, attempt: number, delay: number) => void | Promise<void>;
}

/**
 * Default configuration values.
 */
const DEFAULT_CONFIG: Required<
  Omit<IRetryWithBackoffConfig<any>, 'shouldRetry' | 'onRetry'>
> = {
  retries: 3,
  initialDelay: 100,
  strategy: BackoffStrategy.Exponential,
  factor: 2, // Default factor for Exponential
  maxDelay: Infinity,
  jitter: 0
};

/**
 * Calculates the delay for the next retry attempt based on the configuration.
 *
 * @param attempt - The current attempt number (starting from 1).
 * @param config - The retry configuration.
 * @returns The calculated delay in milliseconds.
 */
function calculateDelay(
  attempt: number,
  config: Required<IRetryWithBackoffConfig<any>>
): number {
  let delay: number;
  const factor =
    config.strategy === BackoffStrategy.Exponential
      ? config.factor
      : // Default factor 1 for Constant/Linear if not specified
        (config.factor ?? 1);

  switch (config.strategy) {
    case BackoffStrategy.Constant:
      delay = config.initialDelay;
      break;
    case BackoffStrategy.Linear:
      delay =
        config.initialDelay + config.initialDelay * factor * (attempt - 1);
      break;
    case BackoffStrategy.Exponential:
    default:
      delay = config.initialDelay * factor ** (attempt - 1);
      break;
  }

  delay = Math.min(delay, config.maxDelay);

  if (config.jitter > 0) {
    // +/- jitter %
    const jitterAmount = delay * config.jitter * (Math.random() * 2 - 1);
    delay += jitterAmount;
  }

  return Math.max(0, delay); // Ensure delay is not negative
}

/**
 * Executes an operation with retry logic based on the provided configuration.
 *
 * @template T - The expected return type of the operation.
 * @template E - The expected error type.
 * @param operation - The asynchronous function to execute and potentially
 * retry.
 * @param config - Optional configuration for the retry behavior.
 * @returns A promise that resolves with the result of the operation if
 * successful, or rejects if all retries fail.
 * @throws {Error} Throws the last error encountered after all retries are
 * exhausted or if shouldRetry returns false.
 */
export async function withBackoffRetry<T, E = Error>(
  operation: () => Promise<T>,
  config?: IRetryWithBackoffConfig<E>
): Promise<T> {
  // Create base config first
  const mergedConfig: Required<IRetryWithBackoffConfig<E>> = {
    ...DEFAULT_CONFIG,
    ...config,
    // Provide default implementations if not present
    shouldRetry: config?.shouldRetry ?? (() => true),
    onRetry: config?.onRetry ?? (() => {})
  };

  // Set factor after merging to ensure correct default
  mergedConfig.factor ??=
    mergedConfig.strategy === BackoffStrategy.Exponential ? 2 : 1;

  let lastError: E | undefined;

  // Awaiting operation attempts, shouldRetry logic, onRetry callbacks, and
  // delays sequentially is desired behavior in the following loop.
  /* eslint-disable no-await-in-loop -- See above. */
  for (let attempt = 0; attempt <= mergedConfig.retries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as E;

      if (attempt === mergedConfig.retries) {
        // Max retries reached
        break;
      }

      const shouldRetry = await Promise.resolve(
        mergedConfig.shouldRetry(lastError)
      );

      if (!shouldRetry) {
        // Should not retry based on the error type or condition
        break;
      }

      const delay = calculateDelay(attempt + 1, mergedConfig);

      await Promise.resolve(
        mergedConfig.onRetry(lastError, attempt + 1, delay)
      );

      if (delay > 0) {
        await wait(delay);
      }
    }
  }
  /* eslint-enable no-await-in-loop */

  // If loop finished without returning, throw the last error
  throw lastError;
}

/**
 * A class-based wrapper for the `withBackoffRetry` functionality. Allows
 * pre-configuring the retry settings and executing multiple operations with the
 * same config.
 *
 * @template E - The expected error type.
 */
export class RetryWithBackoff<E = Error> {
  private readonly config: Required<IRetryWithBackoffConfig<E>>;

  /**
   * Creates an instance of RetryWithBackoff.
   *
   * @param config - Optional configuration for the retry behavior.
   */
  public constructor(config?: IRetryWithBackoffConfig<E>) {
    // Create base config first
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
      // Provide default implementations if not present
      shouldRetry: config?.shouldRetry ?? (() => true),
      onRetry: config?.onRetry ?? (() => {})
    };

    // Set factor after merging to ensure correct default
    this.config.factor ??=
      this.config.strategy === BackoffStrategy.Exponential ? 2 : 1;
  }

  /**
   * Executes an operation with the pre-configured retry logic.
   *
   * @template T - The expected return type of the operation.
   * @param operation - The asynchronous function to execute and potentially
   * retry.
   * @returns A promise that resolves with the result of the operation if
   * successful, or rejects if all retries fail.
   * @throws {Error} Throws the last error encountered after all retries are
   * exhausted or if shouldRetry returns false.
   */
  public async execute<T>(operation: () => Promise<T>): Promise<T> {
    return withBackoffRetry(operation, this.config);
  }
}
